import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import UsefulInfoListing from '../../components/UsefulInfo/UsefulInfoListing';
import { getUsefulInfo } from '../../server/getUsefulInfo';
import { getFaq } from '../../server/getFaq';
import { destinationMiddleware } from '../../server/middleware/destinationMiddleware';
import { sanitizeHtml } from '../../server/utlis/sanitizeHtml';

const loadDestination = createServerFn()
    .middleware([destinationMiddleware])
    .handler(async ({ context }) => {
        return context.destination;
    });

export const Route = createFileRoute('/useful-info/')({
    beforeLoad: async () => {
        try {
            const destination = await loadDestination();
            return { destination };
        } catch (error) {
            console.error('Failed to load destination:', error);
            throw error;
        }
    },
    loader: async ({ context }) => {
        const { destination } = context;
        const [usefulInfoData, faqData] = await Promise.all([
            destination?._id
                ? getUsefulInfo({
                    data: {
                        destinationId: destination._id,
                        page: 1,
                        limit: 30,
                    },
                }).catch(() => ({}))
                : Promise.resolve({}),
            destination?._id
                ? getFaq({
                    data: {
                        destinationId: destination._id,
                        page: 1,
                        limit: 50,
                    },
                }).catch(() => ({}))
                : Promise.resolve({}),
        ]);

        const faqs = await Promise.all(
            (faqData?.docs || []).map(async (faq) => ({
                ...faq,
                answer: await sanitizeHtml({ data: { html: faq.answer } })
                    .catch(() => faq.answer),
            }))
        );

        return { destination, usefulInfoData, faqs };
    },
    // Server-rendered — see routes/index.jsx for why this replaces Helmet.
    head: ({ loaderData }) => {
        const destination = loaderData?.destination;
        const title = `${destination?.name} | Useful Information`;
        const description = `Find up-to-date and useful information about the ${destination?.name}!`;
        const image = destination?.images?.info?.[0]?.url;
        const siteUrl = (destination?.url || '').replace(/\/$/, '');

        return {
            meta: [
                { title },
                { name: 'description', content: description },
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'website' },
                ...(siteUrl ? [{ property: 'og:url', content: `${siteUrl}/useful-info` }] : []),
                ...(image ? [{ property: 'og:image', content: image }] : []),
            ],
        };
    },
    component: UsefulInfoListingPage,
});

function UsefulInfoListingPage() {
    const { usefulInfoData, faqs } = Route.useLoaderData();
    return <UsefulInfoListing articles={usefulInfoData?.docs || []} faqs={faqs} />;
}
